import { LoadingState } from "@/components/UIComponents";
import { HubResponse } from "@/services/agenda/types";
import { HttpError } from "@/services/HttpClient";
import { refStoresService } from "@/services/refStores";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { StoreContextType } from "./types";

const DEFAULT_STORE_ID = 5;

/** One retry per candidate: enough for a blip, never enough to stall the app. */
const RESOLVE_ATTEMPTS = 2;

/** The pre-resolution store: id 0 marks "no store", never a real store id. */
const EMPTY_STORE: HubResponse = {
  id: 0,
  name: "",
  externalCode: "",
  storeCode: "",
  phone: "",
  latitude: "",
  longitude: "",
  address: "",
  district: "",
  county: "",
  zipCode: "",
  isActive: false,
  services: [],
};

/** Shown wherever a flow cannot continue because no store could be resolved. */
export const STORE_UNAVAILABLE_MESSAGE =
  "Não foi possível identificar a loja. Atualize a página e confirme a loja selecionada.";

const StoreContext = createContext<StoreContextType | undefined>(undefined);

/**
 * A 4xx is a verdict about the request itself, so repeating it changes nothing.
 * An unknown hub code answers 400 ("RefStore with Internal Code [...] Not
 * Found"), not 404, so the whole range has to count — except 408, which is
 * how HttpClient reports its own timeout, a transient failure.
 */
const isFinalFailure = (error: unknown): boolean =>
  error instanceof HttpError &&
  error.status >= 400 &&
  error.status < 500 &&
  error.status !== 408;

/**
 * Runs one candidate of the documented store-selection chain (hub_code → saved
 * hubCode → VITE_APPOINTMENT_STORE_ID → default store) and answers `null` when
 * the candidate cannot be used.
 *
 * A transient failure — a network error, the HttpClient timeout, a 5xx — gets a
 * second attempt, so a blip is not mistaken for a missing store. HttpClient
 * rejects its own timeout as an HttpError 408, which `isFinalFailure` exempts
 * from the 4xx rule so it is retried. Cancellation is still detected through
 * `signal.aborted` rather than the error type.
 */
const resolveStore = async (
  loadStore: (signal: AbortSignal) => Promise<HubResponse>,
  source: string,
  signal: AbortSignal,
): Promise<HubResponse | null> => {
  for (let attempt = 1; attempt <= RESOLVE_ATTEMPTS; attempt++) {
    try {
      return await loadStore(signal);
    } catch (error) {
      if (signal.aborted) return null;

      if (isFinalFailure(error) || attempt === RESOLVE_ATTEMPTS) {
        console.error(`Could not resolve store from ${source}:`, error);
        return null;
      }

      console.warn(`Retrying store resolution from ${source}:`, error);
    }
  }

  return null;
};

export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within a StoreProvider");
  }
  return context;
};

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: FC<StoreProviderProps> = ({ children }) => {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const hubCodeFromUrl = urlSearchParams.get("hub_code");

  // `hub_code` is a boot-time input from Hubs, but no route carries search
  // params and the catch-all redirect drops them, so a missing code means "the
  // router rewrote the URL", never "Hubs stopped asking for this store". Latch
  // the last one seen; a full reload is the reset point and re-reads the URL.
  const [urlHubCode, setUrlHubCode] = useState<string | null>(hubCodeFromUrl);

  const [hubCode, setHubCode] = useState<string | null>("");
  const [store, setStore] = useState<HubResponse>(EMPTY_STORE);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasStoreResolutionError, setHasStoreResolutionError] =
    useState<boolean>(false);
  const [allStores, setAllStores] = useState<HubResponse[]>([]);
  const [isLoadingAllStores, setIsLoadingAllStores] = useState<boolean>(false);
  const [selectedStoreInScheduler, setSelectedStoreInScheduler] = useState<
    number | null
  >(null);
  const [selectedSchedulerStore, setSelectedSchedulerStore] =
    useState<HubResponse | null>(null);
  const [isLoadingSchedulerStore, setIsLoadingSchedulerStore] =
    useState<boolean>(false);
  const resolutionGenerationRef = useRef(0);

  useEffect(() => {
    if (hubCodeFromUrl && hubCodeFromUrl !== urlHubCode) {
      setUrlHubCode(hubCodeFromUrl);
    }
  }, [hubCodeFromUrl, urlHubCode]);

  // A resolved store is authoritative: it also overrides a manual pick in the
  // scheduler's store dropdown, so the calendar can never keep pointing at a
  // store the current `hub_code` no longer refers to.
  const applyResolvedStore = (
    resolvedStore: HubResponse,
    resolvedHubCode: string,
  ) => {
    setStore(resolvedStore);
    setHubCode(resolvedHubCode);
    setSelectedStoreInScheduler(resolvedStore.id);
    setHasStoreResolutionError(false);
  };

  // A resolution failure drops the previously resolved store as well, so every
  // consumer agrees that no store is available instead of some flows carrying on
  // with a stale id. Persisted `hubCode`/`storeId` are deliberately left alone:
  // they are inputs for the next attempt, and a reload re-runs the whole chain.
  const applyStoreResolutionFailure = () => {
    setStore(EMPTY_STORE);
    setSelectedStoreInScheduler(null);
    setHubCode(null);
    setHasStoreResolutionError(true);
  };

  useEffect(() => {
    const generation = ++resolutionGenerationRef.current;
    const controller = new AbortController();

    // Runs settle in completion order, not in start order, so a superseded run
    // must never write: that is how a slow lookup for the previous store used to
    // overwrite the store that was just requested.
    const isSuperseded = () =>
      controller.signal.aborted ||
      generation !== resolutionGenerationRef.current;

    setIsLoading(true);

    const extractStoreId = async () => {
      try {
        const hubCodeFromLocalStorage = localStorage.getItem("hubCode");

        // Prefer hub_code from URL whenever it's present (e.g. iframe or deep link)
        if (urlHubCode) {
          const resolvedStore = await resolveStore(
            signal => refStoresService.getStoreByHubCode(urlHubCode, signal),
            `hub_code "${urlHubCode}"`,
            controller.signal,
          );
          if (isSuperseded()) return;

          if (resolvedStore) {
            // Persisted only once the code resolves, so an unusable hub_code
            // cannot overwrite a working saved one.
            localStorage.setItem("hubCode", urlHubCode);
            localStorage.setItem(
              "storeExternalCode",
              resolvedStore.externalCode,
            );
            localStorage.setItem("storeId", resolvedStore.id.toString());
            applyResolvedStore(resolvedStore, urlHubCode);
            return;
          }

          // Hubs asked for one specific store. Substituting another one is how
          // the app ended up silently showing the previous store, so stop here
          // instead of walking the rest of the chain.
          console.error(
            `Could not resolve the store requested by hub_code "${urlHubCode}"`,
          );
          applyStoreResolutionFailure();
          return;
        }

        if (hubCodeFromLocalStorage) {
          const resolvedStore = await resolveStore(
            signal =>
              refStoresService.getStoreByHubCode(
                hubCodeFromLocalStorage,
                signal,
              ),
            `saved hubCode "${hubCodeFromLocalStorage}"`,
            controller.signal,
          );
          if (isSuperseded()) return;

          if (resolvedStore) {
            applyResolvedStore(resolvedStore, hubCodeFromLocalStorage);
            return;
          }
        }

        const envStoreId = Number(import.meta.env?.VITE_APPOINTMENT_STORE_ID);
        if (!isNaN(envStoreId) && envStoreId > 0) {
          const resolvedStore = await resolveStore(
            signal => refStoresService.getStoreById(envStoreId, signal),
            "VITE_APPOINTMENT_STORE_ID",
            controller.signal,
          );
          if (isSuperseded()) return;

          if (resolvedStore) {
            applyResolvedStore(resolvedStore, resolvedStore.storeCode);
            return;
          }
        }

        const defaultStore = await resolveStore(
          signal => refStoresService.getStoreById(DEFAULT_STORE_ID, signal),
          "default store",
          controller.signal,
        );
        if (isSuperseded()) return;

        if (defaultStore) {
          applyResolvedStore(defaultStore, defaultStore.storeCode);
          return;
        }

        console.error(
          "Could not resolve a store from hub_code, saved hubCode, VITE_APPOINTMENT_STORE_ID or the default store",
        );
        applyStoreResolutionFailure();
      } catch (error) {
        if (isSuperseded()) return;
        console.error("Error extracting storeId:", error);
        applyStoreResolutionFailure();
      } finally {
        if (!isSuperseded()) setIsLoading(false);
      }
    };

    extractStoreId();

    return () => {
      controller.abort();
    };
  }, [urlHubCode]);

  useEffect(() => {
    const fetchAllStores = async () => {
      setIsLoadingAllStores(true);
      try {
        const allStoresData: HubResponse[] = [];
        let currentPage = 0;
        const pageSize = 100;
        let hasMorePages = true;

        while (hasMorePages) {
          const response = await refStoresService.getAll(currentPage, pageSize);
          allStoresData.push(...response.content);

          hasMorePages = currentPage + 1 < response.page.totalPages;
          currentPage++;
        }

        setAllStores(allStoresData);
      } catch (error) {
        console.error("Error fetching all stores:", error);
      } finally {
        setIsLoadingAllStores(false);
      }
    };

    fetchAllStores();
  }, []);

  // Fetch full store data when selectedStoreInScheduler changes
  useEffect(() => {
    // Only the store now picked may answer: a store picked earlier that answers
    // late would otherwise put its hours on the calendar of this one.
    let active = true;

    const fetchStoreData = async () => {
      if (!selectedStoreInScheduler) {
        setSelectedSchedulerStore(null);
        setIsLoadingSchedulerStore(false);
        return;
      }

      setIsLoadingSchedulerStore(true);
      try {
        const storeData = await refStoresService.getStoreById(
          selectedStoreInScheduler,
        );
        if (!active) return;
        setSelectedSchedulerStore(storeData);
      } catch (error) {
        if (!active) return;
        console.error("Error fetching store data:", error);
        // Fallback to store from allStores if fetch fails
        const fallbackStore = allStores.find(
          s => s.id === selectedStoreInScheduler,
        );
        if (fallbackStore) {
          setSelectedSchedulerStore(fallbackStore);
        } else {
          setSelectedSchedulerStore(null);
        }
      } finally {
        if (active) setIsLoadingSchedulerStore(false);
      }
    };

    fetchStoreData();
    return () => {
      active = false;
    };
  }, [selectedStoreInScheduler, allStores]);

  // `store` is seeded with an empty placeholder whose id is 0, so 0 means "no
  // store resolved" and must never be sent to the API as a real store id.
  const resolvedStoreId = store.id > 0 ? store.id : null;

  // Until the store now picked answers, the previous pick's data is not shown
  // as this one's: consumers fall back to its entry in `allStores`.
  const currentSchedulerStore =
    selectedSchedulerStore?.id === selectedStoreInScheduler
      ? selectedSchedulerStore
      : null;

  const contextValue: StoreContextType = {
    store,
    resolvedStoreId,
    hubCode,
    hasStoreResolutionError,
    isLoading,
    allStores,
    isLoadingAllStores,
    selectedStoreInScheduler,
    setSelectedStoreInScheduler,
    selectedSchedulerStore: currentSchedulerStore,
    isLoadingSchedulerStore,
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {isLoading ? (
        <LoadingState placement="viewport" label="A preparar aplicação…" />
      ) : (
        children
      )}
    </StoreContext.Provider>
  );
};
